<template>
  <form ref="addOrEditForm" @submit.prevent="$emit('submit', formData)">
    <FormInput
      v-model.trim="formData.name"
      label="Nom de ma veille"
      name="dashboard-name"
      type="text"
      :error="
        !isFormValid && !formData.name
          ? 'Veuillez renseigner le nom de votre veille.'
          : null
      "
      required
    />

    <h6>Filtres appliqués :</h6>
    <RadioButtons
      :options="displayOptions"
      :active-option="activeDisplay"
      @select-option="activeDisplay = $event"
    >
      <template #append-1>
        <span
          v-show="discoveryBadgeValue > 0"
          class="badge"
          :class="{ active: activeDisplay === displayOptions[0].value }"
          >{{ discoveryBadgeValue }}</span
        >
      </template>
      <template #append-2>
        <span
          v-show="allFeaturesFiltersCount > 0"
          class="badge"
          :class="{ active: activeDisplay === displayOptions[1].value }"
          >{{ allFeaturesFiltersCount }}</span
        >
      </template>
    </RadioButtons>

    <p>
      {{
        activeDisplay === DISPLAY_OPTIONS.GENERAL
          ? "Ces filtres s'appliquent à toutes les pages"
          : "Ces filtres s'appliquent uniquement aux pages énoncées"
      }}
    </p>
    <template v-if="activeDisplay === DISPLAY_OPTIONS.GENERAL">
      <CollapseSection start-closed>
        <template #title>
          <div class="title-and-badge">
            <p class="collapse-title">Thématiques</p>
            <span v-show="discoveryBadgeValue > 0" class="badge">
              {{ discoveryBadgeValue }}
            </span>
          </div>
        </template>
        <template #content>
          <div class="thematics">
            <DiscoveryOptions
              :thematics="userStore.thematics"
              :active-thematics="selectedThematics"
              :active-subthematics="selectedSubthematics"
              @add-thematic="addSelectedFilter('thematic_ids', $event)"
              @add-subthematic="addSelectedFilter('subthematic_ids', $event)"
              @remove-thematic="removeSelectedFilter('thematic_ids', $event)"
              @remove-subthematic="
                removeSelectedFilter('subthematic_ids', $event)
              "
              @reset="resetDiscover"
            />
          </div>
        </template>
      </CollapseSection>
      <FormTextarea
        v-model.trim="formData.filters.search"
        label="Requête personnalisée"
        name="dashboard-additional-query"
      />
    </template>

    <template v-else>
      <CollapseSection
        v-for="(featureValue, featureKey) in features"
        :key="featureKey"
        start-closed
        @open="getFilters(featureKey)"
      >
        <template #title>
          <div class="title-and-badge">
            <p class="collapse-title">
              {{ featureValue.label ?? featureValue.name }}
            </p>
            <span v-show="getBadgeValue(featureKey) > 0" class="badge">
              {{ getBadgeValue(featureKey) }}
            </span>
          </div>
        </template>
        <template #content>
          <div v-if="featuresLoading.includes(featureKey)" class="loader">
            <MinerveOrbite />
          </div>
          <div
            v-else-if="Object.keys(featuresFilters).includes(featureKey)"
            class="feature-filters"
          >
            <div v-for="(value, key) in featureValue.filters" :key="key">
              <SearchAsYouTypeFilter
                v-if="key === 'author'"
                filter="author"
                :items="socialMediaStore.authors"
                item-label="name"
                item-value="_id"
                :active-filters="formData.filters?.[featureKey]?.[key] ?? []"
                :is-loading="socialMediaStore.isAuthorFilterLoading"
                @search="fetchSearchAuthors"
                @add-filter-item="addFeatureFilter(featureKey, key, $event)"
                @remove-filter-item="removeFilterItem"
                @reset-filter="resetAuthorFilter"
              >
                {{ value }}
              </SearchAsYouTypeFilter>

              <SelectMultipleCounter
                v-else
                :filter="key"
                :items="featuresFilters[featureKey][key]"
                :active-filters="formData.filters?.[featureKey]?.[key] ?? []"
                has-search
                @add-filter-item="addFeatureFilter(featureKey, key, $event)"
                @remove-filter-item="
                  removeFeatureFilter(featureKey, key, $event)
                "
                @reset-filter="resetFeatureFilter(featureKey, key)"
              >
                {{ value }}
              </SelectMultipleCounter>
            </div>
          </div>
          <div v-else>
            <p>Ces filtres ne sont pas disponibles</p>
          </div>
        </template>
      </CollapseSection>
    </template>
  </form>
</template>

<script setup>
import { useTemplateRef, ref, computed, onMounted } from "vue";
import MinerveOrbite from "@follaw-fr/components/reusable/minerve/MinerveOrbite.vue";
import FormInput from "@follaw-fr/components/reusable/atoms/FormInput.vue";
import RadioButtons from "@follaw-fr/components/reusable/atoms/RadioButtons.vue";
import CollapseSection from "@follaw-fr/components/reusable/atoms/CollapseSection.vue";
import DiscoveryOptions from "@follaw-fr/components/topbar/molecules/DiscoveryOptions.vue";
import FormTextarea from "@follaw-fr/components/reusable/atoms/FormTextarea.vue";
import SelectMultipleCounter from "@follaw-fr/components/filter-menu/molecules/SelectMultipleCounter.vue";
import { useUserStore } from "@follaw-fr/stores/user";
import { useSocialMediaStore } from "@follaw-fr/stores/social-media/posts";

import { useFeaturesFilters } from "@follaw-fr/composables/useFeaturesFilters";
import { features } from "@follaw-fr/utils/features";
import SearchAsYouTypeFilter from "../../filter-menu/molecules/SearchAsYouTypeFilter.vue";

const userStore = useUserStore();
const socialMediaStore = useSocialMediaStore();

const {
  featuresLoading,
  featuresFilters,
  activeFiltersByFeature,
  getFeatureFilters,
} = useFeaturesFilters();

const props = defineProps({
  targetedDashboard: { type: Object, default: null },
  targetedDashboardName: { type: String, default: null },
});

defineEmits(["submit"]);

const fetchSearchAuthors = (nameSearch) => {
  if (nameSearch.length > 0) {
    socialMediaStore.getSearchAuthorsFilters(nameSearch);
  } else {
    socialMediaStore.resetSearchAuthorsFilters();
  }
};

const formData = ref({
  name: "",
  filters: {
    thematic_ids: [...userStore.sharedFilters.selected_thematics],
    subthematic_ids: [...userStore.sharedFilters.selected_subthematics],
    search: userStore.sharedFilters.selected_keyword ?? "",
  },
});
const isFormValid = ref(true);

const DISPLAY_OPTIONS = {
  GENERAL: "generalFilters",
  PAGE: "pageFilters",
};

const activeDisplay = ref(DISPLAY_OPTIONS.GENERAL);
const displayOptions = [
  {
    label: "Filtres généraux",
    value: DISPLAY_OPTIONS.GENERAL,
  },
  {
    label: "Filtres par page",
    value: DISPLAY_OPTIONS.PAGE,
  },
];

// DISCOVERY
// Theses selected* objects are built for the needs of using the id and the name
// in order to display them on the selected filters list
const selectedThematics = computed(() =>
  formData.value.filters.thematic_ids.map((themId) =>
    userStore.thematics.find((t) => t._id === parseInt(themId))
  )
);

const allSubthematics = computed(() => {
  return userStore.thematics.reduce((acc, them) => {
    return [...acc, ...them.subthematics];
  }, []);
});

const selectedSubthematics = computed(() =>
  formData.value.filters.subthematic_ids.map((subThemId) =>
    allSubthematics.value.find((t) => t._id === parseInt(subThemId))
  )
);

const discoveryBadgeValue = computed(
  () => selectedThematics.value.length + selectedSubthematics.value.length
);

const addSelectedFilter = (filter, event) => {
  const newId = event.item._id;
  if (!formData.value.filters[filter].includes(newId)) {
    formData.value.filters[filter].push(newId);
  }
};

const removeSelectedFilter = (filter, event) => {
  const idToRemove = event.item;
  formData.value.filters[filter] = formData.value.filters[filter].filter(
    (id) => id !== idToRemove
  );
};

const resetDiscover = () => {
  formData.value.filters.thematic_ids = [];
  formData.value.filters.subthematic_ids = [];
};

// FEATURES
const getFilters = (feature) => {
  if (!featuresFilters.value[feature]) {
    getFeatureFilters(feature);
  }
};

const addFeatureFilter = (featureKey, filterKey, event) => {
  const itemToAdd = event.item;
  const targetFilters = formData.value.filters?.[featureKey]?.[filterKey] || [];

  formData.value.filters = {
    ...formData.value.filters,
    [featureKey]: {
      ...formData.value.filters[featureKey],
      [filterKey]: [...targetFilters, itemToAdd],
    },
  };
};

const removeFeatureFilter = (featureKey, filterKey, event) => {
  const itemToRemove = event.item;
  const targetFilters = formData.value.filters?.[featureKey]?.[filterKey] || [];

  formData.value.filters[featureKey][filterKey] = targetFilters.filter(
    (item) => item !== itemToRemove
  );
  if (formData.value.filters[featureKey][filterKey].length === 0) {
    delete formData.value.filters[featureKey][filterKey];
  }
  if (!Object.keys(formData.value.filters[featureKey]).length) {
    delete formData.value.filters[featureKey];
  }
};

const resetFeatureFilter = (featureKey, filterKey) => {
  formData.value.filters[featureKey][filterKey] = [];
};

const getBadgeValue = (featureKey) => {
  return (
    formData.value.filters?.[featureKey] &&
    Object.values(formData.value.filters[featureKey])
      .filter((value) => value !== null)
      .reduce((acc, curr) => acc + curr.length, 0)
  );
};

const allFeaturesFiltersCount = computed(() => {
  let sum = 0;
  Object.keys(features).forEach((featureKey) => {
    sum += getBadgeValue(featureKey) ?? 0;
  });
  return sum;
});

const validate = () => {
  isFormValid.value = !!formData.value.name;
  return isFormValid.value;
};

onMounted(() => {
  if (props.targetedDashboard) {
    formData.value.name = props.targetedDashboard.name;
    formData.value.filters = { ...props.targetedDashboard.filters };
  } else {
    if (props.targetedDashboardName) {
      formData.value.name = props.targetedDashboardName;
    }
    const activeFilters = Object.fromEntries(
      Object.entries(activeFiltersByFeature.value).filter(
        // The [,value] syntax is because the entries are needed to recompose the object after filtering but we don't need to use the key in the callback
        ([, value]) => Object.keys(value).length
      )
    );

    Object.entries(activeFilters).forEach(([key, value]) => {
      formData.value.filters[key] = value;
    });
  }
});
const addOrEditForm = useTemplateRef("addOrEditForm");

defineExpose({
  form: addOrEditForm,
  validate,
});
</script>

<style lang="scss" scoped>
p {
  margin: 0;
  line-height: var(--lh-normal);
}

form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 570px;
}

.title-and-badge {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;

  .collapse-title {
    margin: 0;
    font-weight: var(--fw-semibold);
    line-height: var(--lh-normal);
  }
}

.badge {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px 7px;
  border-radius: 28px;
  min-width: 23px;
  height: 23px;
  font-size: var(--size-small);
  font-weight: var(--fw-regular);
  color: white;
  background-color: var(--clr-p8);

  &.active {
    color: var(--clr-p8);
    background-color: var(--clr-p2);
  }
}

.thematics {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70px;
}

.feature-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}
</style>
